import { useEffect, useMemo, useRef, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowDown01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Avatar } from "shared/components/Avatar";
import { Checkbox, CheckboxSize } from "shared/components/forms/Checkbox";
import { BaseInput, InputSize } from "shared/components/forms/BaseInput";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from "shared/components/Tooltip";
import { Show } from "shared/components";
import { formatPhone } from "shared/utils";
import { cx } from "shared/utils/classnames";
import { useComposerNarrow } from "../context/useComposerNarrow";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useInfiniteContactSearch, type CrmContact } from "../api/crm";
import { recipientKey, type Recipient } from "./multi-recipient";

interface ToAddressOption {
  value: string;
  disabled?: boolean;
  disabledMessage?: string;
}

interface ToRecipientsSelectProps {
  channel: "email" | "sms";
  contactId?: number;
  contactName: string;
  addresses: ToAddressOption[];
  selectedAddress: string;
  onSelectAddress: (address: string) => void;
  extraRecipients: Recipient[];
  onAddRecipient: (recipient: Recipient) => void;
  onRemoveRecipient: (recipient: Recipient) => void;
  primaryExcluded: boolean;
  onPrimaryExcludedChange: (excluded: boolean) => void;
}

const contactFullName = (contact: CrmContact) =>
  [contact.first_name, contact.last_name]
    .filter((part) => !!part && part.trim().length > 0)
    .join(" ")
    .trim();

const formatAddress = (channel: "email" | "sms", address: string) =>
  channel === "sms" ? formatPhone(address) : address;

const AVATAR_INITIALS_CLASS = "text-[8px] font-bold";

const BATCH_RECIPIENT_LIMIT = 50;
const CAP_MESSAGE = `You can send to up to ${BATCH_RECIPIENT_LIMIT} recipients at once.`;
const LAST_RECIPIENT_MESSAGE =
  "A message needs at least one recipient — add another recipient first to unselect this one.";

const GroupHeader = ({ name }: { name: string }) => (
  <div className="flex h-10 w-full items-center gap-3 border-b border-alpha-40 bg-background-900 px-4">
    <Avatar name={name} size={22} className={AVATAR_INITIALS_CLASS} />
    <span className="truncate text-sm font-medium text-content-1000">
      {name}
    </span>
  </div>
);

const AddressRow = ({
  channel,
  address,
  checked,
  disabled,
  disabledMessage,
  locked,
  lockedMessage,
  onToggle
}: {
  channel: "email" | "sms";
  address: string;
  checked: boolean;
  disabled?: boolean;
  disabledMessage?: string;
  locked?: boolean;
  lockedMessage?: string;
  onToggle: () => void;
}) => {
  const row = (
    <div
      className={cx(
        "border-b border-alpha-40 px-4 py-2.5 last:border-b-0",
        disabled || locked ? "opacity-60" : "hover:bg-alpha-40"
      )}
    >
      <Checkbox
        size={CheckboxSize.SM}
        checked={checked}
        disabled={disabled || locked}
        onChange={onToggle}
        label={formatAddress(channel, address)}
        labelClassName="text-sm font-medium text-content-500 truncate"
        wrapperClassName={
          disabled || locked ? "cursor-not-allowed" : "cursor-pointer"
        }
      />
    </div>
  );

  let reason: string | undefined;
  if (disabled) reason = disabledMessage;
  else if (locked) reason = lockedMessage;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>{row}</div>
      </TooltipTrigger>
      <TooltipContent className="max-w-[280px] rounded-lg border bg-alpha-800 p-3 text-sm text-white z-50">
        <div className="break-all font-medium">
          {formatAddress(channel, address)}
        </div>
        {reason ? <div className="mt-1">{reason}</div> : null}
      </TooltipContent>
    </Tooltip>
  );
};

export const ToRecipientsSelect = ({
  channel,
  contactId,
  contactName,
  addresses,
  selectedAddress,
  onSelectAddress,
  extraRecipients,
  onAddRecipient,
  onRemoveRecipient,
  primaryExcluded,
  onPrimaryExcludedChange
}: ToRecipientsSelectProps) => {
  const isNarrow = useComposerNarrow();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const [knownContacts, setKnownContacts] = useState<
    Map<number, { name: string; addresses: string[] }>
  >(new Map());

  const searching = debouncedSearch.trim().length > 0;
  const {
    data: searchData,
    isLoading: isSearching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteContactSearch(debouncedSearch, {
    enabled: open,
    per_page: 10
  });

  const handleListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 80) return;
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  };

  const listedContacts = useMemo(
    () =>
      (searchData?.pages.flatMap((page) => page.items) ?? []).filter(
        (contact) => contact.id !== contactId
      ),
    [searchData, contactId]
  );

  const extraKeys = useMemo(
    () =>
      new Set(
        extraRecipients.map((recipient) =>
          recipientKey(recipient.contactId, recipient.address)
        )
      ),
    [extraRecipients]
  );

  const allSelected: Recipient[] = useMemo(
    () => [
      ...(contactId !== undefined && selectedAddress && !primaryExcluded
        ? [{ contactId, contactName, address: selectedAddress }]
        : []),
      ...extraRecipients
    ],
    [contactId, contactName, selectedAddress, extraRecipients, primaryExcluded]
  );

  const atCap = allSelected.length >= BATCH_RECIPIENT_LIMIT;

  const extraGroups = useMemo(() => {
    const groups = new Map<number, { name: string; addresses: string[] }>();
    extraRecipients.forEach((recipient) => {
      if (recipient.contactId === contactId) return;
      const known = knownContacts.get(recipient.contactId);
      if (known) {
        groups.set(recipient.contactId, known);
        return;
      }
      const group = groups.get(recipient.contactId) ?? {
        name: recipient.contactName,
        addresses: []
      };
      if (!group.addresses.includes(recipient.address))
        group.addresses.push(recipient.address);
      groups.set(recipient.contactId, group);
    });
    return groups;
  }, [extraRecipients, contactId, knownContacts]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const isChecked = (ownerId: number, address: string) =>
    (ownerId === contactId &&
      address === selectedAddress &&
      !primaryExcluded) ||
    extraKeys.has(recipientKey(ownerId, address));

  const toggleOwnAddress = (address: string) => {
    if (contactId === undefined) return;
    if (address === selectedAddress) {
      if (primaryExcluded) {
        onPrimaryExcludedChange(false);
        return;
      }
      const replacement = extraRecipients.find(
        (recipient) =>
          recipient.contactId === contactId && recipient.address !== address
      );
      if (replacement) {
        onRemoveRecipient(replacement);
        onSelectAddress(replacement.address);
        return;
      }
      if (extraRecipients.length > 0) onPrimaryExcludedChange(true);
      return;
    }
    const key = recipientKey(contactId, address);
    if (extraKeys.has(key)) {
      onRemoveRecipient({ contactId, contactName, address });
      return;
    }
    if (primaryExcluded) {
      onSelectAddress(address);
      onPrimaryExcludedChange(false);
      return;
    }
    if (!atCap) onAddRecipient({ contactId, contactName, address });
  };

  const toggleOtherAddress = (
    contact: { id: number; name: string },
    address: string
  ) => {
    const recipient = {
      contactId: contact.id,
      contactName: contact.name,
      address
    };
    if (extraKeys.has(recipientKey(contact.id, address)))
      onRemoveRecipient(recipient);
    else if (!atCap) onAddRecipient(recipient);
  };

  const toggleSearchedAddress = (contact: CrmContact, address: string) => {
    const name = contactFullName(contact) || `Contact #${contact.id}`;
    const channelAddresses =
      (channel === "sms" ? contact.phones : contact.emails) ?? [];
    setKnownContacts((prev) =>
      new Map(prev).set(contact.id, { name, addresses: channelAddresses })
    );
    toggleOtherAddress({ id: contact.id, name }, address);
  };

  return (
    <div className="relative min-w-0" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cx(
          "flex h-10 min-w-0 items-center gap-2 rounded-lg border border-alpha-100 bg-white px-2 shadow-xs hover:bg-alpha-40",
          isNarrow ? "w-auto shrink-0" : "w-full max-w-[220px]"
        )}
      >
        <Show if={allSelected.length <= 1}>
          <Avatar
            name={allSelected[0]?.contactName || contactName}
            size={20}
            className={cx(AVATAR_INITIALS_CLASS, "shrink-0")}
          />
          <Show if={!isNarrow}>
            <span className="min-w-0 flex-1 truncate text-left text-[13px] font-medium text-content-1000">
              {allSelected[0]
                ? formatAddress(channel, allSelected[0].address)
                : "Select..."}
            </span>
          </Show>
        </Show>
        <Show if={allSelected.length > 1}>
          <span className="flex items-center">
            {allSelected.slice(0, 2).map((recipient, index) => (
              <span
                key={recipientKey(recipient.contactId, recipient.address)}
                className={cx(
                  "rounded-full",
                  index > 0 && "-ml-[5px] ring-1 ring-background-900"
                )}
              >
                <Avatar
                  name={recipient.contactName || recipient.address}
                  size={20}
                  className={AVATAR_INITIALS_CLASS}
                />
              </span>
            ))}
          </span>
          <span className="shrink-0 text-[13px] font-medium text-content-1000">
            {allSelected.length}
          </span>
        </Show>
        <HugeiconsIcon
          icon={ArrowDown01Icon}
          size={16}
          className="ml-auto shrink-0 text-content-300"
        />
      </button>

      <Show if={open}>
        <div
          className="absolute bottom-full right-0 z-50 mb-2 max-h-96 w-80 overflow-auto rounded-lg border border-alpha-100 bg-white shadow-xl"
          onScroll={handleListScroll}
        >
          <div className="p-3">
            <BaseInput
              id="to-recipients-search"
              size={InputSize.SM}
              type="text"
              placeholder="Search"
              value={search}
              onChange={setSearch}
              autoComplete="off"
              autoFocus
            />
          </div>

          <Show if={!searching}>
            <Show if={contactId !== undefined && addresses.length > 0}>
              <GroupHeader name={contactName || "Contact"} />
              {addresses.map((option) => {
                const lockedPrimary =
                  option.value === selectedAddress &&
                  !primaryExcluded &&
                  allSelected.length === 1;
                const checked = isChecked(contactId ?? -1, option.value);
                const capped = atCap && !checked;
                let disabledMessage = option.disabledMessage;
                if (!option.disabled && capped) disabledMessage = CAP_MESSAGE;
                return (
                  <AddressRow
                    key={option.value}
                    channel={channel}
                    address={option.value}
                    checked={checked}
                    disabled={option.disabled || capped}
                    disabledMessage={disabledMessage}
                    locked={lockedPrimary}
                    lockedMessage={LAST_RECIPIENT_MESSAGE}
                    onToggle={() => toggleOwnAddress(option.value)}
                  />
                );
              })}
            </Show>

            {[...extraGroups.entries()]
              .filter(
                ([groupContactId]) =>
                  !listedContacts.some(
                    (contact) => contact.id === groupContactId
                  )
              )
              .map(([groupContactId, group]) => (
                <div key={groupContactId}>
                  <GroupHeader
                    name={group.name || `Contact #${groupContactId}`}
                  />
                  {group.addresses.map((address) => {
                    const checked = isChecked(groupContactId, address);
                    const capped = atCap && !checked;
                    return (
                      <AddressRow
                        key={address}
                        channel={channel}
                        address={address}
                        checked={checked}
                        disabled={capped}
                        disabledMessage={capped ? CAP_MESSAGE : undefined}
                        locked={checked && allSelected.length === 1}
                        lockedMessage={LAST_RECIPIENT_MESSAGE}
                        onToggle={() =>
                          toggleOtherAddress(
                            { id: groupContactId, name: group.name },
                            address
                          )
                        }
                      />
                    );
                  })}
                </div>
              ))}
          </Show>

          <Show if={isSearching}>
            <p className="px-4 py-3 text-sm text-content-300">Searching…</p>
          </Show>
          <Show if={searching && !isSearching && listedContacts.length === 0}>
            <p className="px-4 py-3 text-sm text-content-300">
              No contacts found.
            </p>
          </Show>
          <Show if={!isSearching}>
            {listedContacts.map((contact) => {
              const contactAddresses =
                (channel === "sms" ? contact.phones : contact.emails) ?? [];
              if (contactAddresses.length === 0) return null;
              const name = contactFullName(contact) || `Contact #${contact.id}`;
              return (
                <div key={contact.id}>
                  <GroupHeader name={name} />
                  {contactAddresses.map((address) => {
                    const checked = isChecked(contact.id, address);
                    const capped = atCap && !checked;
                    return (
                      <AddressRow
                        key={address}
                        channel={channel}
                        address={address}
                        checked={checked}
                        disabled={capped}
                        disabledMessage={capped ? CAP_MESSAGE : undefined}
                        locked={checked && allSelected.length === 1}
                        lockedMessage={LAST_RECIPIENT_MESSAGE}
                        onToggle={() => toggleSearchedAddress(contact, address)}
                      />
                    );
                  })}
                </div>
              );
            })}
            <Show if={isFetchingNextPage}>
              <p className="px-4 py-3 text-sm text-content-300">
                Loading more…
              </p>
            </Show>
          </Show>
        </div>
      </Show>
    </div>
  );
};
